import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import avatar from 'assets/images/avatar.png';
import pdfIcon from 'assets/images/pdf-icon.png';
import SectionDivider from 'modules/shared/common/components/SectionDivider';
import Popup from 'modules/shared/common/components/Popup';

import {
  FileUploaderFilesWrapper,
  FileUploaderFileItemPadding,
  FileUploaderFileImg,
  FileUploaderResizedFileImg,
  FileUploaderImgDeleteOverlay,
  FileUploaderImgDeleteIcon,
  FileUploaderFileItemWrapper,
  FileUploaderImgPreview,
  FileUploaderBtn,
  FileUploaderLabelWrapper,
  FileUploaderLabel,
} from './FileUploader.style';

const FileItem = ({ file, togglePreviewImg, handleFileItemDeleteIconClick }) => {
  const { dataUrl, shouldBeProcessed, isProcessed, dimensions } = file;
  const { dimenParamToChange, ratio } = dimensions;

  let imgSrc = dataUrl || avatar;
  let fileOpacity = (isProcessed && 0.6) || 0.5;
  let fileBlur = (isProcessed && 3) || 0;
  if (shouldBeProcessed === false) {
    imgSrc = pdfIcon;
    fileOpacity = 1;
    fileBlur = 0;
  }

  return (
    <FileUploaderFileItemWrapper onClick={() => togglePreviewImg(true, file)}>
      <FileUploaderFileItemPadding />
      <FileUploaderFileImg src={imgSrc} opacity={fileOpacity} blur={fileBlur} />
      {isProcessed && (
        <FileUploaderResizedFileImg
          src={imgSrc}
          dimenParamToChange={dimenParamToChange}
          ratio={ratio}
        />
      )}
      <FileUploaderImgDeleteOverlay />
      <FileUploaderImgDeleteIcon onClick={event => handleFileItemDeleteIconClick(event, file)} />
    </FileUploaderFileItemWrapper>
  );
};
FileItem.propTypes = {
  file: PropTypes.shape({
    dataUrl: PropTypes.string,
    shouldBeProcessed: PropTypes.bool,
    isProcessed: PropTypes.bool,
    dimensions: PropTypes.shape({
      dimenParamToChange: PropTypes.string,
      ratio: PropTypes.number,
    }),
  }).isRequired,
  togglePreviewImg: PropTypes.func.isRequired,
  handleFileItemDeleteIconClick: PropTypes.func.isRequired,
};

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const SUPPORTED_MIME_TYPES_REGEX = /image|pdf/i;
const MIME_TYPES_NOT_TO_BE_PROCESSED = ['application/pdf'];
const MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION = ['application/pdf'];
const MAX_IMG_WIDTH = 800;
const MAX_IMG_HEIGHT = 800;
const IMG_QUALITY = 0.7;
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const FileUploader = ({ orderResultsArr, setUploadedData }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [previewFileIndex, setPreviewFileIndex] = useState(0);
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const fileInput = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  const createWindowUrlFromFile = file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);
    return fileUrl;
  };

  const createImageFromFile = fileUrl =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.src = fileUrl;

      image.onload = () => {
        resolve(image);
      };

      image.onerror = reject;
    });

  const resizeAndCompressImage = async image => {
    const { width, height } = image;
    const compressionDimensions = {};
    let dimenParamToChange = '';
    let ratio = 0;

    if (width > height) {
      const maxWidth = Math.min(width, MAX_IMG_WIDTH);
      compressionDimensions.width = maxWidth;
      compressionDimensions.height = Math.round((height * maxWidth) / width);
      dimenParamToChange = 'height';
      ratio = Math.round((height / width) * 10) / 10;
    } else {
      const maxHeight = Math.min(height, MAX_IMG_HEIGHT);
      compressionDimensions.width = Math.round((width * maxHeight) / height);
      compressionDimensions.height = maxHeight;
      dimenParamToChange = 'width';
      ratio = Math.round((width / height) * 10) / 10;
    }

    const dimensions = {
      dimenParamToChange,
      ratio,
    };

    const canvas = document.createElement('canvas');
    canvas.width = compressionDimensions.width;
    canvas.height = compressionDimensions.height;
    const canvasCtx = canvas.getContext('2d');
    canvasCtx.drawImage(image, 0, 0, compressionDimensions.width, compressionDimensions.height);

    return {
      compressedImageCanvas: canvas,
      dimensions,
    };
  };

  const createFileFromCanvas = (canvas, fileName, fileType) => {
    const imageDataUrl = canvas.toDataURL(fileType, IMG_QUALITY);
    const imageDataUrlArr = imageDataUrl.split(',');
    const imageMimeType = imageDataUrlArr[0].match(/:(.*?);/)[1];
    const imageBase64 = imageDataUrlArr[1];
    const imageDecodedBase64 = atob(imageBase64);
    let imageDecodedBase64Length = imageDecodedBase64.length;
    const imageBinaryDataArr = new Uint8Array(imageDecodedBase64Length);
    // eslint-disable-next-line no-plusplus
    while (imageDecodedBase64Length--) {
      imageBinaryDataArr[imageDecodedBase64Length] =
        imageDecodedBase64.charCodeAt(imageDecodedBase64Length);
    }
    const compressedImageFile = new File([imageBinaryDataArr], fileName, { type: imageMimeType });

    return {
      compressedImageFile,
      imageDataUrl,
    };
  };

  const processFile = async file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);

    const image = await createImageFromFile(fileUrl);

    let compressedImageCanvas = image;
    let dimensions = {};
    if (file.shouldBypassCompression === false)
      ({ compressedImageCanvas, dimensions } = await resizeAndCompressImage(image));

    const { compressedImageFile, imageDataUrl } = createFileFromCanvas(
      compressedImageCanvas,
      file.fileName,
      file.fileType,
    );

    const processedFileObj = {
      ...file,
      dataUrl: imageDataUrl,
      isProcessed: true,
      compressedFileData: compressedImageFile,
      dimensions,
    };

    return processedFileObj;
  };

  const prepareUploadedFilesForProcessing = async uploadedFiles => {
    const uploadedFilesArr = [...uploadedFiles];

    for (let i = 0; i < uploadedFilesArr.length; i += 1) {
      const currentFile = uploadedFilesArr[i];

      if (currentFile.isProcessed === false) {
        // we should use promise.all instead
        // eslint-disable-next-line no-await-in-loop
        uploadedFilesArr[i] = await processFile(currentFile);
      } else {
        const fileDataUrl = createWindowUrlFromFile(currentFile);
        uploadedFilesArr[i] = {
          ...currentFile,
          dataUrl: fileDataUrl,
        };
      }
    }

    setUploadedData(uploadedFilesArr);
  };
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const togglePreviewImg = (openPopup, file) => {
    if (openPopup) {
      if (file.shouldBeProcessed === false) {
        window.open(window.URL.createObjectURL(file.fileData));
      } else {
        const fileIndex = orderResultsArr.indexOf(file);
        setPreviewFileIndex(fileIndex);
        setIsPreviewFilePopupOpen(true);
      }
    } else setIsPreviewFilePopupOpen(false);
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleFileInputChange = event => {
    const uploadedFiles = event.target.files;
    const orderResults = [...orderResultsArr];

    for (let i = 0; i < uploadedFiles.length; i += 1) {
      const currentFile = uploadedFiles[i];
      const { name, type } = currentFile;

      const isFileTypeSupported = SUPPORTED_MIME_TYPES_REGEX.test(type);

      if (isFileTypeSupported && orderResultsArr.some(file => file.fileName === name) === false) {
        const shouldBeProcessed = MIME_TYPES_NOT_TO_BE_PROCESSED.includes(type) === false;
        const shouldBypassCompression = MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION.includes(type);

        const fileObj = {
          fileData: currentFile,
          fileName: name,
          fileType: type,
          dataUrl: '',
          shouldBeProcessed,
          isProcessed: !shouldBeProcessed,
          shouldBypassCompression,
          compressedFileData: (shouldBypassCompression && currentFile) || null,
          dimensions: {},
        };

        orderResults.push(fileObj);
      }
    }

    if (fileInput && fileInput.current) fileInput.current.value = '';
    setUploadedData(orderResults);
    prepareUploadedFilesForProcessing(orderResults);
  };

  const handleFileItemDeleteIconClick = (event, file) => {
    if (event) event.stopPropagation();

    const orderResults = [...orderResultsArr];
    const currentFileIndex = orderResults.indexOf(file);
    orderResults.splice(currentFileIndex, 1);
    setUploadedData(orderResults);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  const imgPreviewFile = orderResultsArr[previewFileIndex];

  return (
    <>
      <FileUploaderFilesWrapper>
        {orderResultsArr.map(file => (
          <FileItem
            key={file.fileName}
            file={file}
            togglePreviewImg={togglePreviewImg}
            handleFileItemDeleteIconClick={handleFileItemDeleteIconClick}
          />
        ))}
      </FileUploaderFilesWrapper>
      {isPreviewFilePopupOpen && (
        <Popup handleCloseBtnClick={() => togglePreviewImg()}>
          <FileUploaderImgPreview src={imgPreviewFile.dataUrl} />
        </Popup>
      )}
      <SectionDivider gap={30} />
      <FileUploaderBtn
        id="file-uploader-btn"
        type="file"
        multiple
        accept="image/*,application/pdf"
        ref={fileInput}
        onChange={event => handleFileInputChange(event)}
      />
      <FileUploaderLabelWrapper>
        <FileUploaderLabel as="label" htmlFor="file-uploader-btn">
          Attach Files
        </FileUploaderLabel>
      </FileUploaderLabelWrapper>
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

FileUploader.propTypes = {
  orderResultsArr: PropTypes.arrayOf(
    PropTypes.shape({
      dataUrl: PropTypes.string,
    }),
  ).isRequired,
  setUploadedData: PropTypes.func.isRequired,
};

FileUploader.defaultProps = {};

export default FileUploader;
